// dashboard/src/headcount.js
// Billable vs non-billable headcount. Float has no billable tag, so billable is a property of the
// ROLE: a populated bill rate or cost rate means billable, blank means non-billable (not of current
// project assignment — someone on the bench is still billable).

// Float can't express part-time %, and work_days_hours misses reduced hours spread over 5 days.
// Add a name here to pin an FTE; everyone else is derived from work_days_hours.
export const FTE_OVERRIDES = { 'Jack Littleworth': 0.8 };

const STD_WEEK_HRS = 40;
const hrs = n => parseFloat(n || 0);

export const isEmployee     = p => p.employee_type === 1; // excludes contractors and placeholders
export const isBillableRole = p => hrs(p.default_hourly_rate) > 0 || hrs(p.cost_rate) > 0;

export function fteOf(p) {
  if (FTE_OVERRIDES[p.name] != null) return FTE_OVERRIDES[p.name];
  const week = (p.work_days_hours || [0, 8, 8, 8, 8, 8, 0]).reduce((s, h) => s + hrs(h), 0);
  return Math.min(1, week / STD_WEEK_HRS);
}

const isoDay = d => d.toISOString().slice(0, 10);

// Employed on `day` ('YYYY-MM-DD'): started on/before it and not yet ended.
const employedOn = (p, day) => (!p.start_date || p.start_date <= day) && (!p.end_date || p.end_date > day);

const sum = (people, pick) => Math.round(people.reduce((s, p) => s + pick(p), 0) * 10) / 10;

/**
 * Month-end headcount (FTE-weighted) from January to the current month.
 * `people` must include leavers (with end_date) or earlier months undercount.
 */
export function headcountByMonth(people, today = new Date()) {
  const emps = people.filter(isEmployee);
  const y = today.getFullYear();
  return Array.from({ length: today.getMonth() + 1 }, (_, m) => {
    const monthEnd = new Date(Date.UTC(y, m + 1, 0));
    const day = isoDay(monthEnd) > isoDay(today) ? isoDay(today) : isoDay(monthEnd); // current month = today
    const here = emps.filter(p => employedOn(p, day));
    const bill = here.filter(isBillableRole);
    return {
      month: new Date(Date.UTC(y, m, 1)).toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' }),
      billable:    sum(bill, fteOf),
      nonBillable: sum(here.filter(p => !isBillableRole(p)), fteOf),
      total:       sum(here, fteOf),
    };
  });
}

/** Today's headcount by role, plus employees with a future start date ("planned"). */
export function headcountByRole(people, roleName, today = new Date()) {
  const day = isoDay(today);
  const rows = {};
  people.filter(isEmployee).forEach(p => {
    const planned = p.start_date && p.start_date > day;
    if (!planned && !employedOn(p, day)) return;
    const role = roleName(p) || 'Unassigned';
    const r = rows[role] || (rows[role] = { role, billable: 0, nonBillable: 0, planned: 0, plannedNames: [] });
    if (planned) { r.planned += fteOf(p); r.plannedNames.push(p.name); }
    else r[isBillableRole(p) ? 'billable' : 'nonBillable'] += fteOf(p);
  });
  return Object.values(rows)
    .map(r => ({ ...r, billable: sum([r], x => x.billable), nonBillable: sum([r], x => x.nonBillable), planned: sum([r], x => x.planned) }))
    .sort((a, b) => (b.billable + b.nonBillable + b.planned) - (a.billable + a.nonBillable + a.planned));
}
